import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { workbenchStore } from '~/lib/stores/workbench';
import { Modal } from './Modal';
import { X, Loader2 } from 'lucide-react';
import { updateGithubStatus, openDatabase, clearGithubConnection } from '~/lib/persistence/db';
import { db, projectId } from '~/lib/persistence/useChatHistory';
import axios from 'axios';
import {
  githubBranchStore,
  githubOwnerStore,
  githubRepoStore,
  setAllGithubData,
  clearGithubStores,
} from '~/lib/stores/integrations';
import * as Sentry from '@sentry/remix';
import { validateRepoOrBranchName } from '~/utils/github';
import { StatusDisplay } from './StatusDisplay';
import type { ConnectionData } from '~/services/interfaceService';
import { projectsFromIDB } from '~/lib/stores/storageStatusStore';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface CreateRepoModalProps {
  isOpen: boolean;
  onClose: () => void;
  setIsGitRepo: (isGitRepo: boolean) => void;
}

export const CreateRepoModal = ({ isOpen, onClose, setIsGitRepo }: CreateRepoModalProps) => {
  const { t } = useTranslation();
  console.log('>>>[CreateRepoModal] isOpen: ', isOpen);

  const [repoName, setRepoName] = useState('');
  const [isPrivate, setIsPrivate] = useState(true); // Default to true
  const [isCreating, setIsCreating] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [nameAvailability, setNameAvailability] = useState<'available' | 'taken' | 'invalid' | null>(null);
  const [owner, setOwner] = useState('');
  const [error, setError] = useState<string>('');
  const [userName, setUserName] = useState<string>('');

  // Debounce logic for auto-checking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (repoName.trim()) {
        checkRepoName();
      } else {
        setNameAvailability(null);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [repoName]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setRepoName('');
    setIsPrivate(true); // Reset to true when opening modal
    setIsCreating(false);
    setIsChecking(false);
    setNameAvailability(null);
    setError('');

    const token = selectionStore.get().githubAccessToken;

    if (!token) {
      setUserName('');
      return;
    }

    fetchUserDetails(token)
      .then((user) => {
        if (user?.login) {
          setUserName(user.login);
          githubOwnerStore.set(user.login);
        } else {
          setUserName('');
        }
      })
      .catch(() => setUserName(''));
  }, [isOpen]);

  const checkRepoName = async () => {
    if (!validateRepoOrBranchName(repoName)) {
      setNameAvailability('invalid');
      return false;
    }

    try {
      setIsChecking(true);

      const token = selectionStore.get().githubAccessToken;
      const userDetails = await fetchUserDetails(token);

      if (!userDetails) {
        localStorage.removeItem('githubAccessToken');
        throw new Error('Failed to fetch user details');
      }

      const owner = userDetails?.login;
      return await Sentry.startSpan({ name: 'createRepoModal.checkRepoName' }, async () => {
        const response = await fetch(`https://api.github.com/repos/${owner}/${repoName}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('>>>[CreateRepoModal] response: ', response);

        if (response.status === 404) {
          setNameAvailability('available');
          return true;
        } else {
          setNameAvailability('taken');
          setOwner(owner);
          return false;
        }
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          function: 'Error in checkRepoName',
          repoName,
          owner,
        },
      });
      console.error('Error checking repo name:', error);
      setNameAvailability('invalid');
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  const fetchUserDetails = async (token: string) => {
    try {
      return await Sentry.startSpan({ name: 'createRepoModal.fetchUserDetails' }, async () => {
        const { data } = await axios.get('https://api.github.com/user', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/vnd.github+json',
          },
        });

        return data;
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          function: 'Error in fetchUserDetails',
          token,
        },
      });
      console.error('Error fetching user details:', error);
      return false;
    }
  };

  const handleDisconnectGithub = async () => {
    // Add confirmation to prevent accidental disconnects
    if (
      !window.confirm(
        t('modals.createRepo.disconnectConfirm', 'Are you sure you want to disconnect your GitHub account?'),
      )
    ) {
      return;
    }

    try {
      const dbInstance = await openDatabase();
      const pId = projectId.get();

      if (!dbInstance || !pId) {
        throw new Error('Failed to open database');
      }

      // cloudRevert (burst)
      // await deleteProjectConnection(pId, 'github');
      // await updateIsGitStatus(pId, false);
      await clearGithubConnection(dbInstance, pId);
      dbInstance.close();
      clearGithubStores();
      setIsGitRepo(false);
      selectionStore.setKey('githubAccessToken', '');
      localStorage.removeItem('githubAccessToken');
      localStorage.removeItem('githubAccessTokenExpiry');
      localStorage.removeItem('githubAccessTokenExpiresAt');
      toast.success('GitHub account disconnected successfully', { autoClose: 1000 });
      onClose();

      // revalidate();

      // Prompt user to reconnect
      const event = new CustomEvent('github:login:required');
      window.dispatchEvent(event);
      console.log('>>>[CreateRepoModal] github:login:required event dispatched');
    } catch (err) {
      console.error('Failed to disconnect GitHub:', err);
      toast.error('Failed to disconnect GitHub repository', { autoClose: 3000 });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (nameAvailability !== 'available' && nameAvailability === 'invalid') {
        setError('Repository name is not available. Please choose a different name.');
        return;
      } else if (!nameAvailability) {
        const isAvailable = await checkRepoName();

        if (!isAvailable) {
          setError('Repository name is not available. Please choose a different name.');
          return;
        }
      } else {
        setNameAvailability('available');
      }

      setIsCreating(true);

      const token = selectionStore.get().githubAccessToken;
      const files = await workbenchStore.getAllFiles();
      console.log('>>>[CreateRepoModal] files: ', files);

      const response = await fetch('/api/github', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'createRepo',
          repoName,
          isPrivate,
          files,
        }),
      });

      const data = (await response.json()) as { error?: string; repoUrl?: string };

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create repository');
      }

      const pId = projectId.get();

      if (db && pId) {
        const token = selectionStore.get().githubAccessToken;

        const { login } = await fetchUserDetails(token);
        const ownerName = login;

        const connectionData: ConnectionData['connectionData'] = {
          repoName,
          ownerName,
          branch: 'main',
        };

        updateGithubStatus(db, pId, {
          github: connectionData,
        });

        // cloudRevert (burst)
        // await updateProjectConnection(pId, {
        //   connectionType: 'github',
        //   connectionData,
        // });

        // await updateIsGitStatus(pId, true);

        // await updateProjectInStore(pId, {
        //   isGitRepo: true,
        // });

        setAllGithubData(ownerName, repoName, 'main');
        setIsGitRepo(true);

        await projectsFromIDB();
      }

      toast.success('Repository created successfully!', { autoClose: 1000 });
      onClose();

      if (data.repoUrl) {
        window.open(data.repoUrl, '_blank');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create repository', { autoClose: 3000 });
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const hasGithubConnection = Boolean(githubOwnerStore.get());
  const displayUser = userName || '—';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="fixed inset-0 z-50" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-full px-4 max-w-[480px]">
        <div className="bg-alpha-background-secondary rounded-xl border border-white/10 relative w-full overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b border-white/10">
            <h2 className="text-base font-semibold text-[#e7e7e7] m-0">Create New Repository</h2>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="p-1 text-[#e7e7e7] bg-transparent border-none rounded-md hover:bg-white/10 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="px-6 py-3 border-b flex flex-row gap-2 justify-between items-center border-white/10">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-[#e7e7e7]/70">
                  <span className="i-ph-user text-[14px]" />
                  <span>{t('modals.pullRequest.connectedUser')} </span>
                  <span className="font-medium text-[#e7e7e7]">{displayUser}</span>
                </div>
                {hasGithubConnection && (
                  <button
                    onClick={handleDisconnectGithub}
                    className="px-2 py-1 text-xs bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 transition-colors rounded-md flex items-center gap-1"
                    title={t('modals.createRepo.disconnectTooltip', 'Disconnect your GitHub account')}
                  >
                    <span className="i-ph-sign-out-bold text-[12px]" />
                    {t('modals.createRepo.disconnect', 'Sign Out')}
                  </button>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-3">
              <label className="block text-[#e7e7e7]/80 text-sm font-medium">Repository Name</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={repoName}
                  onChange={(e) => {
                    const filteredValue = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 40);
                    setRepoName(filteredValue);

                    // Reset availability while typing to avoid showing stale state
                    if (nameAvailability !== 'available') {
                      setNameAvailability(null);
                    }

                    setError('');
                  }}
                  className={`flex-1 px-4 py-2.5 text-[#e7e7e7] bg-transparent border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm transition-all ${
                    nameAvailability === 'taken' || nameAvailability === 'invalid'
                      ? 'border-red-500 bg-red-500/5'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  placeholder="my-awesome-project"
                  required
                />
                {/* Check Availability button removed as per your change */}
              </div>

              {/* Only show loading or error states */}
              <div className="mt-1.5">
                {isChecking ? (
                  <p className="text-xs text-[white]/50 flex items-center gap-1.5">
                    <Loader2 size={12} className="animate-spin" />
                    Checking availability...
                  </p>
                ) : nameAvailability && nameAvailability !== 'available' ? (
                  <p className="text-sm text-red-500 flex items-center gap-1.5">
                    {nameAvailability === 'taken' ? (
                      <>
                        <span className="i-ph-x-circle-fill text-[14px]" />
                        Repository name is already taken
                      </>
                    ) : (
                      <>
                        <span className="i-ph-warning-circle-fill text-[14px]" />
                        Repository name cannot contain spaces or special characters. Only letters, numbers, hyphens (-),
                        and underscores (_) are allowed.
                      </>
                    )}
                  </p>
                ) : null}
              </div>
            </div>

            <div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 justify-start">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPrivate}
                    onClick={() => setIsPrivate(!isPrivate)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      isPrivate ? 'bg-blue-600' : 'bg-[#2a2b2e]'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        isPrivate ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <div className="flex flex-col gap-1">
                    <span className="font-medium text-[#e7e7e7] text-sm">
                      This is a {isPrivate ? 'Private' : 'Public'} Repository
                    </span>
                    <p className="text-xs text-[#e7e7e7]/50">
                      {isPrivate ? 'Only visible to you and people you share with' : 'Visible to everyone'}
                    </p>
                  </div>
                </div>
              </div>
              {error && <StatusDisplay status="error" message={error} />}
            </div>

            <div className="flex justify-end gap-3 mt-8 pt-2 border-t border-white/5">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-[#e7e7e7] bg-transparent border border-white/10 rounded-lg hover:bg-white/5 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className="px-4 py-2.5 bg-linear-to-b from-alpha-primary-start to-alpha-primary-end rounded-lg text-black bg-white text-sm font-medium hover:brightness-110 transition-all disabled:opacity-50 shadow-sm shadow-blue-500/20"
              >
                {isCreating ? (
                  <span className="flex  items-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Creating...
                  </span>
                ) : (
                  'Create Repository'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
